import type { Tables } from "../database.types";

export type MentionType = "user" | "project" | "ticket";
export type NoteWithChildNotes = Tables<"notes"> & {
	childNotes: Tables<"notes">[] | null;
};

/**
 * The things one can write a note on, and the whole of them.
 *
 * A note carries no reference to its parent — the `notes` row is text, author, organisation,
 * thread and a flag — so which of the twenty-one join tables a note hangs in is what decides who may
 * read it. `/notes/:notesTable/:identifier` takes that table from the URL, which makes this list
 * the surface itself rather than a convenience: each value names a parent whose read right the
 * route checks.
 *
 * Two of the twenty-one are absent on purpose. `substitutionPlanProjectItemNotes` has no caller in
 * either client. `projectTimelineNotes` is a diary entry rather than a comment thread — it points
 * at a project through `projectId` and carries a date of its own — and the projects router serves
 * it; the notes it collects hang in `projectTimelineNoteNotes`, which is on the list.
 *
 * The server's registry (`apps/server/src/handlers/notes/registry.ts`) declares one entry per
 * value — parent table, identifier column, parent subject — and does not compile without it.
 */
export const NOTES_TABLES = [
	"contactNotes",
	"incomingInvoiceNotes",
	"orderNotes",
	"organizationPartnershipNotes",
	"organizationServiceNotes",
	"projectNotes",
	"projectTimelineNoteNotes",
	"protocolNotes",
	"returnOrderNotes",
	"serviceCalculationItemNotes",
	"serviceCalculationNotes",
	"suborderNotes",
	"substitutionPlanNotes",
	"ticketAssignmentNotes",
	"transferCarrierNotes",
	"userNotes",
	"vehicleNotes",
	"warehouseZoneNotes",
	"wikiPageNotes",
] as const;

export type NotesTable = (typeof NOTES_TABLES)[number];

export type NoteWithFiles = Tables<"notes"> & {
	_files: (Tables<"noteFiles"> & {
		file: Tables<"files">;
	})[];
};
